/**
 * `@tagadapay/analytics/registry`: the tracking plan as code (spec I). Pure
 * TypeScript, no runtime dependency: loaded by the root Next app (tsconfig
 * `paths`), jest (`moduleNameMapper`), the PostHog sync scripts and the Vite apps.
 */

import { EVENT_DEFS, REGISTRY, type RegistryEventDef, type RegistryEventName } from './events';
import type { PropsOf } from './props';

export { APPS, AREAS, VERBS, RESERVED_AREAS, EVENT_NAME_REGEX, PII_KEY_REGEX, PERSON_PII_EXCEPTIONS, SAFE_AI_DOMAINS } from './apps';
export type { App, Area, Verb } from './apps';
export { p, defineEvent, defineOutcome, defineWorkflow, ERROR_KINDS, FAIL_PROPS } from './props';
export type { ErrorKind, EventDef, EventInput, EventSource, EventStatus, PropSpec, PropValue, PropsOf } from './props';
export { EVENT_DEFS, REGISTRY } from './events';
export type { RegistryEventName, RegistryEventDef } from './events';
export { LEGACY_EVENTS, LEGACY_V2_CLIENT_EVENTS, LEGACY_EXTERNAL_EVENTS } from './legacy';
export { ACTIVATION_LADDER } from './activation';
export {
  HOSTS,
  HOSTS_GENERATED_AT,
  TAGADA_OWNED_DOMAINS,
  environmentFromHost,
  environmentFromVercelEnv,
  isTagadaOwnedHost,
} from './hosts';
export type { Environment, HostProject } from './hosts';
export { isStaff } from './staff';
export {
  REPLAY_TRIGGER_GROUPS,
  REPLAY_HOSTS_PLACEHOLDER,
  replayHosts,
  replayHostPattern,
  replayTriggerGroups,
} from './replay';
export type { ReplayProject, TriggerGroup } from './replay';

/** Props object of a registry event; nullable props are optional. */
export type RegistryEventProps<E extends RegistryEventName> = PropsOf<RegistryEventDef<E>['props']>;

/** Registry events the server emits (`captureServer`). */
export type ServerEventName = Extract<(typeof EVENT_DEFS)[number], { source: 'server' }>['name'];

/** Registry events a browser emits (`capture`). */
export type ClientEventName = Extract<(typeof EVENT_DEFS)[number], { source: 'client' }>['name'];

const NAMES: ReadonlySet<string> = new Set(Object.keys(REGISTRY));

export function isRegisteredEvent(name: string): name is RegistryEventName {
  return NAMES.has(name);
}
